import React, { useEffect, useState } from 'react';
import api from '../../services/api';

function Card({ title, stats }) {
  const s = stats || {};
  const pos = (s.totalGainPct ?? 0) >= 0;
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${pos ? 'bg-emerald-50/60 border-emerald-100' : 'bg-rose-50/60 border-rose-100'}`}>
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className={`text-3xl font-bold tabular-nums mt-2 ${pos ? 'text-emerald-700' : 'text-rose-700'}`}>
        {(s.totalGainPct ?? 0) >= 0 ? '+' : ''}{s.totalGainPct ?? 0}%
      </p>
      {s.winRatePct != null && (
        <p className="text-xs font-semibold text-slate-700 mt-1.5">
          {s.winRatePct}% win rate <span className="font-normal text-slate-400">(hit full target)</span>
        </p>
      )}
      <p className="text-xs text-slate-500 mt-1">
        {s.closedCount ?? 0} closed · {s.wins ?? 0} closed non-negative · {s.losses ?? 0} losses
        · avg {(s.avgGainPct ?? 0) >= 0 ? '+' : ''}{s.avgGainPct ?? 0}%
      </p>
    </div>
  );
}

function BacktestBarCheck({ stats }) {
  const s = stats || {};
  if (s.closedCount == null) return null;
  const tooSmall = s.closedCount < 10;
  const meeting = s.winRatePct != null && s.winRatePct >= (s.backtestBarPct ?? 60);
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${
      tooSmall ? 'bg-slate-50 border-slate-200' : meeting ? 'bg-emerald-50/60 border-emerald-100' : 'bg-amber-50/60 border-amber-200'
    }`}>
      <p className="text-sm font-semibold text-slate-800">Live vs. backtest bar</p>
      <p className="text-3xl font-bold tabular-nums mt-2 text-slate-800">
        {s.winRatePct != null ? `${s.winRatePct}%` : '—'}
        <span className="text-sm font-normal text-slate-400 ml-2">vs {s.backtestBarPct ?? 60}% bar</span>
      </p>
      <p className="text-xs text-slate-500 mt-2">
        {s.closedCount ?? 0} closed signal{s.closedCount === 1 ? '' : 's'} · avg {(s.avgGainPct ?? 0) >= 0 ? '+' : ''}{s.avgGainPct ?? 0}%
      </p>
      {s.note && <p className="text-xs text-amber-700 mt-2">{s.note}</p>}
      {!tooSmall && !meeting && (
        <p className="text-xs text-amber-700 mt-2">
          Live win rate is running below the backtest bar these signals were gated on — worth
          investigating (regime shift, execution gap, or the backtest sample was too optimistic).
        </p>
      )}
    </div>
  );
}

function EngineSection({ title, note, tag, stats }) {
  const items = stats?.items || [];
  return (
    <div className="mt-6">
      <h2 className="text-lg font-bold mb-1">{title}</h2>
      {note && <p className="text-xs text-slate-500 mb-3">{note}</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Card title="1 month" stats={stats?.month} />
        <Card title="Quarter (3 months)" stats={stats?.quarter} />
        <Card title="6 months" stats={stats?.sixMonths} />
        <Card title="1 year" stats={stats?.year} />
      </div>
      {items.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-6">
          <div className="px-4 py-2.5 bg-slate-50 border-b text-sm font-semibold">Recent {tag} closes (last 30 days)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase text-slate-400">
                <tr>
                  <th className="text-left px-4 py-2">Symbol</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Entry</th>
                  <th className="text-right px-4 py-2">Exit</th>
                  <th className="text-right px-4 py-2">Gain %</th>
                  <th className="text-left px-4 py-2">Closed</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className="border-t border-slate-50">
                    <td className="px-4 py-2 font-semibold">{it.symbol}</td>
                    <td className="px-4 py-2">{it.signalType?.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-2">{it.status}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{it.entryPrice != null ? `$${it.entryPrice}` : '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{it.exitPrice != null ? `$${it.exitPrice}` : '—'}</td>
                    <td className={`px-4 py-2 text-right tabular-nums font-semibold ${it.gainPct >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {it.gainOverridePct != null ? `${it.gainOverridePct >= 0 ? '+' : ''}${it.gainOverridePct}% (override)` : `${it.gainPct >= 0 ? '+' : ''}${it.gainPct}%`}
                    </td>
                    <td className="px-4 py-2 text-slate-400">{it.endDate ? new Date(it.endDate).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPerformance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  const load = () => {
    api.get('/performance/admin')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const runAuto = async () => {
    setMsg('');
    try {
      const r = await api.post('/performance/admin/run-auto-close');
      const d = r.data || {};
      setMsg(
        d.message ||
        `Auto-close ran · ${d.closed ?? 0} closed (manual ${d.manualClosed ?? 0}, AI Rapid ${d.aiRapidClosed ?? 0})`
      );
      load();
    } catch (_) {
      setMsg('Auto-close failed');
    }
  };

  if (loading) return <p className="text-center text-slate-400 py-16">Loading…</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Signal P/L</h1>
          <p className="text-sm text-slate-500">Manual signals + separate AI Rapid Profit performance</p>
        </div>
        <button type="button" onClick={runAuto}
          className="text-sm bg-slate-900 text-white px-4 py-2 rounded-xl">
          Run auto-close now
        </button>
      </div>
      {msg && <p className="text-xs text-slate-500">{msg}</p>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card title="1 month" stats={data?.month} />
        <Card title="Quarter (3 months)" stats={data?.quarter} />
        <Card title="6 months" stats={data?.sixMonths} />
        <Card title="1 year" stats={data?.year} />
      </div>

      <div>
        <h2 className="text-lg font-bold mb-3">AI Rapid Profit P/L</h2>
        <p className="text-xs text-slate-500 mb-3">Closed AI Rapid signals only — separate from manual signals</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Card title="1 month" stats={data?.aiRapid?.month} />
          <Card title="Quarter (3 months)" stats={data?.aiRapid?.quarter} />
          <Card title="6 months" stats={data?.aiRapid?.sixMonths} />
          <Card title="1 year" stats={data?.aiRapid?.year} />
        </div>
        <BacktestBarCheck stats={data?.aiRapid?.year} />
      </div>

      {data?.aiDaily && (
        <div className="mt-6">
          <h2 className="text-lg font-bold mb-1">AI Daily Signals — live vs. backtest</h2>
          <p className="text-xs text-slate-500 mb-3">
            &quot;AI: 1–2 signals today&quot; picks are gated on a 60% historical win rate before they're
            created. This is the only honest check on whether that bar is holding up once real price
            action plays out — not a guarantee, a monitor.
          </p>
          <BacktestBarCheck stats={data.aiDaily} />
        </div>
      )}

      {data?.scalp && (
        <div className="mt-6">
          <h2 className="text-lg font-bold mb-1">Intraday Scalp P/L</h2>
          <p className="text-xs text-slate-500 mb-3">
            Closed scalp option picks only. No backtest bar to compare against here — see the Signals
            page for why — this is just real closed P/L as targets/stops actually get hit.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Card title="1 month" stats={data.scalp.month} />
            <Card title="Quarter (3 months)" stats={data.scalp.quarter} />
            <Card title="6 months" stats={data.scalp.sixMonths} />
            <Card title="1 year" stats={data.scalp.year} />
          </div>
          {(data.scalp.items || []).length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-6">
              <div className="px-4 py-2.5 bg-slate-50 border-b text-sm font-semibold">Recent scalp closes (last 30 days)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase text-slate-400">
                    <tr>
                      <th className="text-left px-4 py-2">Symbol</th>
                      <th className="text-left px-4 py-2">Type</th>
                      <th className="text-left px-4 py-2">Status</th>
                      <th className="text-right px-4 py-2">Entry</th>
                      <th className="text-right px-4 py-2">Exit</th>
                      <th className="text-right px-4 py-2">Gain %</th>
                      <th className="text-left px-4 py-2">Closed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.scalp.items.map(it => (
                      <tr key={it.id} className="border-t border-slate-50">
                        <td className="px-4 py-2 font-semibold">{it.symbol}</td>
                        <td className="px-4 py-2">{it.signalType?.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-2">{it.status}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{it.entryPrice != null ? `$${it.entryPrice}` : '—'}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{it.exitPrice != null ? `$${it.exitPrice}` : '—'}</td>
                        <td className={`px-4 py-2 text-right tabular-nums font-semibold ${it.gainPct >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {it.gainPct >= 0 ? '+' : ''}{it.gainPct}%
                        </td>
                        <td className="px-4 py-2 text-slate-400">{it.endDate ? new Date(it.endDate).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      {data?.dailyFormula && (
        <EngineSection
          title="Daily Formula P/L"
          note="Closed [DAILY] Buy/Short Formula picks only. Real closed P/L as targets/stops get hit."
          tag="Daily Formula"
          stats={data.dailyFormula}
        />
      )}

      {data?.multiHorizonFormula && (
        <EngineSection
          title="Multi-Horizon Formula P/L"
          note="Closed [MHF] picks only, across all four horizon tiers (1-3 Day, 1 Week, 15 Day, 1 Month)."
          tag="Multi-Horizon"
          stats={data.multiHorizonFormula}
        />
      )}

      {data?.intradayOptionFormula && (
        <EngineSection
          title="Intraday Option Formula P/L"
          note="Closed [IOF] Scalp + Swing option picks only."
          tag="Intraday Option"
          stats={data.intradayOptionFormula}
        />
      )}

      {(data?.aiRapid?.items || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-6">
          <div className="px-4 py-2.5 bg-slate-50 border-b text-sm font-semibold">AI Rapid closed trades</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase text-slate-400">
                <tr>
                  <th className="text-left px-3 py-2">Symbol</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Gain %</th>
                  <th className="text-right px-3 py-2">Closed</th>
                </tr>
              </thead>
              <tbody>
                {(data?.aiRapid?.items || []).map(it => (
                  <tr key={`ai-${it.id}`} className="border-t border-slate-50">
                    <td className="px-3 py-2 font-semibold">{it.symbol}</td>
                    <td className="px-3 py-2 text-xs">{it.signalType?.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-xs">{it.status}</td>
                    <td className={`px-3 py-2 text-right font-bold ${(it.gainPct ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {(it.gainPct ?? 0) >= 0 ? '+' : ''}{it.gainPct}%
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-slate-500">
                      {it.endDate ? new Date(it.endDate).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b text-sm font-semibold">Manual signals — by month</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-slate-400">
              <tr>
                <th className="text-left px-3 py-2">Month</th>
                <th className="text-right px-3 py-2">Closed</th>
                <th className="text-right px-3 py-2">W / L</th>
                <th className="text-right px-3 py-2">Total %</th>
                <th className="text-right px-3 py-2">Avg %</th>
              </tr>
            </thead>
            <tbody>
              {(data?.byMonth || []).map(m => (
                <tr key={m.month} className="border-t border-slate-50">
                  <td className="px-3 py-2 font-medium">{m.month}</td>
                  <td className="px-3 py-2 text-right">{m.closedCount}</td>
                  <td className="px-3 py-2 text-right">
                    <span className="text-emerald-600">{m.wins}</span>
                    {' / '}
                    <span className="text-rose-600">{m.losses}</span>
                  </td>
                  <td className={`px-3 py-2 text-right font-bold ${(m.totalGainPct ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {(m.totalGainPct ?? 0) >= 0 ? '+' : ''}{m.totalGainPct}%
                  </td>
                  <td className={`px-3 py-2 text-right ${(m.avgGainPct ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {(m.avgGainPct ?? 0) >= 0 ? '+' : ''}{m.avgGainPct}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
